import React from 'react';
import { mergeProps, useField } from 'react-aria';

import type { CheckboxInputProps } from '../../components/CheckboxInput/CheckboxInput';
import type { BaseFieldProps } from './common/types';
import { Alert } from '../../components/Alert/Alert';
import { Badge } from '../../components/Badge/Badge';
import { CheckboxInput } from '../../components/CheckboxInput/CheckboxInput';
import { colors, darkThemeSelector, styled } from '../../stitches.config';
import filterErrorMessage from '../../utils/filterErrorMessage';
import { SecondaryFieldDescription, SecondaryFieldLabel } from './common/FieldLabel';
import { useCustomFieldProps } from './createCustomFieldPropsProvider';
import { MaybeFieldContainer } from './FieldContainer';

const SecondaryCheckboxFieldParent = styled('div', {
  display: 'flex',
  flexDirection: 'row',
  gap: '$8',
  width: '100%',
});

const SecondaryCheckboxFieldLabelling = styled('div', {
  position: 'relative',
  display: 'flex',
  flexDirection: 'row',
  gap: '$6',
});

const SecondaryCheckboxFieldStart = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '$2',
  width: '100%',
});

const SecondaryCheckboxFieldEnd = styled('div', {
  maxHeight: '$20',
});

const SecondaryCheckboxFieldAlert = styled(Alert, {
  padding: '$8 $16',
});

const SecondaryCheckboxFieldContainer = styled('div', {
  position: 'relative',
  zIndex: 1,
  display: 'flex',
  flexDirection: 'row',
  gap: '$8',
  padding: '$10 $16',

  '&:not(:last-child)::after': {
    content: '',
    position: 'absolute',
    display: 'block',
    right: '$16',
    bottom: '-0.5px',
    left: '$16',
    height: '1px',
    background: colors.strokeNeutralLight,

    [darkThemeSelector]: {
      background: colors.strokeNeutralDark,
    },
  },
});

export interface SecondaryCheckboxFieldProps extends BaseFieldProps, CheckboxInputProps {
  internal?: boolean;
}

export function SecondaryCheckboxField(props: SecondaryCheckboxFieldProps) {
  const { fieldProps: customFieldProps, inputProps: customInputProps } = useCustomFieldProps();

  const {
    description,
    errorMessage = customFieldProps.errorMessage,
    internal,
    label = customFieldProps.label,
    ...extraInputProps
  } = props;

  const {
    fieldProps: inputProps,
    descriptionProps,
    errorMessageProps,
    labelProps,
  } = useField({
    description,
    errorMessage,
    label,
  });

  return (
    <MaybeFieldContainer>
      <SecondaryCheckboxFieldContainer>
        <SecondaryCheckboxFieldParent>
          <SecondaryCheckboxFieldEnd>
            <CheckboxInput {...mergeProps(customInputProps, inputProps, extraInputProps)} />
          </SecondaryCheckboxFieldEnd>
          <SecondaryCheckboxFieldStart>
            <SecondaryCheckboxFieldLabelling>
              <SecondaryFieldLabel {...labelProps}>{label}</SecondaryFieldLabel>
              {internal && (
                <Badge ends="card" size="small" internal>
                  Internal
                </Badge>
              )}
            </SecondaryCheckboxFieldLabelling>
            {description && (
              <SecondaryFieldDescription {...descriptionProps}>
                {description}
              </SecondaryFieldDescription>
            )}
          </SecondaryCheckboxFieldStart>
        </SecondaryCheckboxFieldParent>
      </SecondaryCheckboxFieldContainer>
      {errorMessage && (
        <SecondaryCheckboxFieldAlert
          {...errorMessageProps}
          type="inline"
          relation="stacked"
          variant="negative"
          heading={filterErrorMessage(errorMessage)}
        />
      )}
    </MaybeFieldContainer>
  );
}
